import {AxiosError} from "axios";
import {useClient} from "../../utils/hooks";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
  normalizeV2CreateScheduleResponse,
  normalizeV2SchedulesResponse,
  normalizeSessionsListResponse,
} from "../availability/availability-normalizer";
import {message} from "antd";
import {AnalyticsEvent} from "../../utils/analytics";
import {
  AddDaysToScheduleApiPayload,
  ApplyScheduleToSessionApiPayload,
  CreateUserScheduleApiPayload,
  CreateUserScheduleApiResponse,
  CreateUserSchedulesApiResponse,
  DeleteUserScheduleApiPayload,
  DuplicateUserScheduleApiPayload,
  FetchSessionsNotUsingScheduleApiResponse,
  FetchUserScheduleApiResponse,
  RemoveSessionFromScheduleApiPayload,
  RenameUserScheduleApiPayload,
  SetDefaultUserScheduleApiPayload,
} from "./types";
import {GET_COACH_SESSION_OFFERINGS_QUERY_KEY} from "../session";

const GET_SCHEDULES_QUERY_KEY = "user_schedules";
const GET_SCHEDULE_QUERY_KEY = "user_schedule";
/**
 * Key of the aggregate availability query in `react-query-hooks/availability.ts`.
 * That query is cached with `staleTime: Infinity` and never refetches on focus, and the
 * endpoint behind it merges every schedule the coach owns — so any mutation that changes
 * a schedule's days must invalidate it explicitly or the Availability page serves
 * pre-edit data until a full page reload.
 */
const GET_AVAILABILITY_QUERY_KEY = "availability";

export const useGetUserSchedules = (page = 1, perPage = 50) => {
  const client = useClient("AVAILABILITY");

  return useQuery<
    CreateUserSchedulesApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_SCHEDULES_QUERY_KEY, {page, perPage}],
    queryFn: async () => {
      const path = `availability/schedules?page=${page}&per_page=${perPage}`;
      const data = await client(path);
      const inner = data?.data ?? data;
      return normalizeV2SchedulesResponse(inner);
    },
    refetchOnWindowFocus: false,
  });
};
export const useGetUserSchedule = (scheduleId: string) => {
  const client = useClient("AVAILABILITY");

  return useQuery<
    FetchUserScheduleApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_SCHEDULE_QUERY_KEY, {scheduleId}],
    queryFn: () =>
      client(`availability/schedule/${scheduleId}`).then((data) => data.data),
    refetchOnWindowFocus: false,
    enabled: !!scheduleId,
  });
};

export const useCreateUserScheduleApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();

  return useMutation<
    {data: CreateUserScheduleApiResponse},
    AxiosError<{message: string}>,
    CreateUserScheduleApiPayload,
    unknown
  >({
    mutationFn: async (userSchedulePayload: CreateUserScheduleApiPayload) => {
      const res = await client("availability/schedule", {
        method: "POST",
        data: userSchedulePayload,
      });
      const inner = res?.data ?? res;
      return normalizeV2CreateScheduleResponse(inner);
    },

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Schedule create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Schedule create success");
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_SCHEDULES_QUERY_KEY],
      }),
  });
};

export const useDuplicateUserScheduleApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();
  return useMutation<
    {data: CreateUserScheduleApiResponse},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    DuplicateUserScheduleApiPayload,
    unknown
  >({
    mutationFn: (userSchedulePayload: DuplicateUserScheduleApiPayload) =>
      client(`availability/schedule/${userSchedulePayload.scheduleId}/duplicate`, {
        method: "POST",
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Schedule duplicate fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Schedule duplicate success");
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_SCHEDULES_QUERY_KEY],
        }),
        // The copy's slots join the aggregate availability view.
        queryClient.invalidateQueries({queryKey: [GET_AVAILABILITY_QUERY_KEY]}),
      ]),
  });
};

export const useRenameUserScheduleApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();

  return useMutation<
    {data: Record<string, unknown>},
    AxiosError<{message: string}>,
    RenameUserScheduleApiPayload,
    unknown
  >({
    mutationFn: async (userSchedulePayload: RenameUserScheduleApiPayload) => {
      const res = await client("availability/schedule/rename", {
        method: "POST",
        data: userSchedulePayload,
      });
      return {data: res?.data ?? res ?? {}};
    },

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Rename schedule fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Rename schedule success");
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_SCHEDULES_QUERY_KEY],
      }),
  });
};

export const useDeleteUserScheduleApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();
  return useMutation<
    {data: null},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    DeleteUserScheduleApiPayload,
    unknown
  >({
    mutationFn: (userSchedulePayload: DeleteUserScheduleApiPayload) =>
      client(`availability/schedule`, {
        method: "DELETE",
        data: userSchedulePayload,
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Schedule delete fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Schedule delete success");
      // message.success(`Schedule has been deleted successfully`);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_SCHEDULES_QUERY_KEY],
        }),
        // Sessions move to `newScheduleId`, so both schedules' details change.
        queryClient.invalidateQueries({queryKey: [GET_SCHEDULE_QUERY_KEY]}),
        queryClient.invalidateQueries({queryKey: [GET_AVAILABILITY_QUERY_KEY]}),
      ]),
  });
};
export const useSetDefaultUserScheduleApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();
  return useMutation<
    {data: null},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    SetDefaultUserScheduleApiPayload,
    unknown
  >({
    mutationFn: (userSchedulePayload: SetDefaultUserScheduleApiPayload) =>
      client(`availability/schedule/default`, {
        method: "POST",
        data: userSchedulePayload,
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Schedule default fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Schedule default success");
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_SCHEDULES_QUERY_KEY],
      }),
  });
};

export const useFetchSessionsNotUsingSchedule = (scheduleId: string) => {
  const client = useClient("AVAILABILITY");

  return useQuery<
    FetchSessionsNotUsingScheduleApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [
      GET_SCHEDULES_QUERY_KEY,
      {module: "sessions_not_using_schedule", scheduleId},
    ],
    queryFn: async () => {
      const data = await client(
        `availability/schedule/${scheduleId}/sessions/not-assigned`
      );
      return normalizeSessionsListResponse(data?.data ?? data);
    },
    refetchOnWindowFocus: false,
    enabled: !!scheduleId,
  });
};

export const useFetchSessionsUsingSchedule = (scheduleId: string) => {
  const client = useClient("AVAILABILITY");

  return useQuery<
    FetchSessionsNotUsingScheduleApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_SCHEDULES_QUERY_KEY, {module: "sessions_using_schedule", scheduleId}],
    queryFn: async () => {
      const data = await client(`availability/schedule/${scheduleId}/sessions`);
      return normalizeSessionsListResponse(data?.data ?? data);
    },
    refetchOnWindowFocus: false,
    enabled: !!scheduleId,
  });
};

export const useApplyScheduleToSessionApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();
  return useMutation<
    {data: {}},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    ApplyScheduleToSessionApiPayload,
    unknown
  >({
    mutationFn: (payload: ApplyScheduleToSessionApiPayload) =>
      client(`availability/schedule/session`, {
        method: "POST",
        data: payload,
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Apply schedule to session fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Apply schedule to session success");
    },
    onSettled: (d, e, v) =>
      Promise.all([
        queryClient.refetchQueries({
          queryKey: [GET_COACH_SESSION_OFFERINGS_QUERY_KEY, String(v.sessionId)],
        }),
        // queryClient.invalidateQueries({queryKey: [GET_COACH_SESSION_OFFERINGS_QUERY_KEY]}),
        queryClient.invalidateQueries({
          queryKey: [
            GET_SCHEDULES_QUERY_KEY,
            {module: "sessions_using_schedule", scheduleId: v.scheduleId},
          ],
        }),
        queryClient.invalidateQueries({queryKey: [GET_SCHEDULES_QUERY_KEY]}),
      ]),
  });
};
export const useRemoveSessionFromScheduleApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();
  return useMutation<
    {data: {}},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    RemoveSessionFromScheduleApiPayload,
    unknown
  >({
    mutationFn: (payload: RemoveSessionFromScheduleApiPayload) =>
      client(`availability/schedule/session`, {
        method: "DELETE",
        data: {
          sessionId: payload.sessionId,
        },
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Remove session from schedule fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Remove session from schedule success");
    },
    onSettled: (d, e, v) =>
      Promise.all([
        // queryClient.invalidateQueries({queryKey: [GET_COACH_SESSION_OFFERINGS_QUERY_KEY]}),
        queryClient.refetchQueries({
          queryKey: [GET_COACH_SESSION_OFFERINGS_QUERY_KEY, String(v.sessionId)],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            GET_SCHEDULES_QUERY_KEY,
            {module: "sessions_not_using_schedule", scheduleId: v.scheduleId},
          ],
        }),
      ]),
  });
};
export const useAddDaysToScheduleApi = () => {
  const client = useClient("AVAILABILITY");
  const queryClient = useQueryClient();

  return useMutation<
    {data: Record<string, unknown>},
    AxiosError<{message: string}>,
    AddDaysToScheduleApiPayload,
    unknown
  >({
    mutationFn: (payload: AddDaysToScheduleApiPayload) =>
      client("availability/schedule/dates", {
        method: "POST",
        data: payload,
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Availability", "Add days to schedule fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Availability", "Add days to schedule success");
    },
    onSettled: (d, e, v) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_SCHEDULE_QUERY_KEY, {scheduleId: v.scheduleId}],
        }),
        queryClient.invalidateQueries({queryKey: [GET_AVAILABILITY_QUERY_KEY]}),
      ]),
  });
};
